import * as React from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import trans from "trans"
import Chart from 'react-apexcharts'
import debounce from "lodash/debounce";

import {
    css,
    Section,
    Grid,
    GridCell,
    Theme,
    DataTable,
    DataCell,
    DataBody,
    DataRow,
    Icon,
    PopupHint,
} from "@bdfx/mergado-ui-kit"

import {
    eshopId,
    getProjectSettings,
    getProblems,
    getWarnings,
    getRecommendations,
    getOtherDiagnostics,
    projectId,
    saveProjectSettings as apiSaveProjectSettings,
} from "app/helpers/api";

import { GENERAL_ERROR, UPDATE_SETTINGS } from "../template-edit/reducers";
import { initForm } from "app/reducers/form"
import { intboolean } from "app/types/app-types";
import store from "../store";
import { emptyFiltersState } from "../products/types/filtertypes";
import { iconDisabledColor } from "../template-edit/components/tools-panel";
import { createToast } from "../actions";
import toastFactory from "app/helpers/toast_factory";
import { SettingsFormType } from "app/types/model-types";
import { reversedBarOptions, RowType, UglyYellow } from "app/helpers/diagnostics";
import { inIframe } from "app/helpers/mergado";
import { RouterType, withRouter } from "app/components/routes";
import { FadeLoader } from "react-spinners";
import { AxiosResponse } from "axios";
import { createProductsExport } from "app/reducers/exports"

export type ShowGroupType = 'same_images' | 'empty_images' | 'poor_quality_images' | 'all' | 'unavailable' |
    '200_0' | '200_1' | '200_420' | '200_435' | '200_465' | '500_0' |
    'invalid_url' | 'unsupported_mime_type' | 'transparent_background' | 'has_watermark' | 'wrong_placement' | 'low_quality' | 'not_recommended_colorspace' |
    'unsupported_background' | 'too_many_alts' | 'seo_large_file' | 'few_alt_images' | '' | 'has_person'

export type DiagnosticsPerTemplate = {
    template_id: number
    templateName: string
    isActive: intboolean
    totalProducts: number
    totalImages: number
    totalAltImages: number
    totalProcessedAltImages: number
    statusOk: number
    statusError: number
    missingImages: number
    emptyElements: number
    checkedElements: string[]
}

export type DiagnosticsPerAltTemplate = {
    altTemplateId: number
    altTemplateName: string
    isActive: intboolean
    processedAltImages: number
    statusOk: number
    statusError: number
}

export type ComparisonEngineType = {
    id: number
    name: string
    icon: string
    color: string
    image: string
    allowed_mime_types: string
    no_watermark: intboolean
    no_transparent_bg: intboolean
    no_dark_bg: intboolean
    no_light_bg: intboolean
    unicolor_bg: intboolean
    no_logo_nor_text: intboolean
    max_alt_images: number
    unique_alternatives: intboolean
    recommended_width: number
    recommended_height: number
    max_kilo_pixels: number
    max_kb_size: number
    bg_color: string
    min_compression_quality: number
}

export type ProblemsType = {
    missingImagesTotal: number
    unavailableStatusTotal: number
    unsupportedFormatOrEmptyTotal: number
    problemsCount: number
}

export type WarningsType = {
    invalidUrlTotal: number
    withWatermarksTotal: number
    tooManyAltImagesTotal: number
    unsupportedProjectMimeTypeTotal: boolean
    unsupportedBackgroundTotal: number
    warningsCount: number
    supportedFormats: string
    projectOutputImageFormat: string
    project_type: string
    comparisonEngine: ComparisonEngineType
}

export type RecommendationsType = {
    badPlacementTotal: number
    poorQualityTotal: number
    seoLargeFilesTotal: number
    cmykColorSpaceTotal: number
    hasPersonTotal: number
    sameImagesTotal: number
    fewAltImagesTotal: number
    recommendationsCount: number
}

export type OtherDiagnostics = {
    project_id: number
    totalProducts: number
    outputImageElement: string
    isOutputElementHidden: boolean
}

interface Props {
    router: RouterType
    createProductsExport: typeof createProductsExport
    initForm: typeof initForm
}

interface State {
    otherDiagnostics: OtherDiagnostics
    problems: ProblemsType
    warnings: WarningsType
    recommendations: RecommendationsType
    projectSettings: SettingsFormType
}

const iconSize = 28

class Diagnostics extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            otherDiagnostics: null,
            recommendations: null,
            warnings: null,
            problems: null,
            projectSettings: null,
        }
    }

    componentDidMount() {
        getProblems()
            .then(r => this.setState({ problems: r.data }))
            .catch(error => console.debug('problems', error))

        getRecommendations()
            .then(r => this.setState({ recommendations: r.data }))
            .catch(error => console.debug('recommendations', error))

        getWarnings()
            .then(r => this.setState({ warnings: r.data }))
            .catch(error => console.debug('warnings', error))

        getOtherDiagnostics()
            .then(r => this.setState({ otherDiagnostics: r.data }))
            .catch(error => console.debug('other', error))

        getProjectSettings()
            .then((response: AxiosResponse<SettingsFormType>) => {
                this.setState({ projectSettings: response.data })
            })
            .catch(error => {
                console.error('cannot get project settings', error)
            })
    }

    updateSettingsDebounced = debounce(() => {
        apiSaveProjectSettings(this.state.projectSettings)
            .then((response: any) => {
                store.dispatch(createToast(toastFactory(UPDATE_SETTINGS.SUCCESS, response.data)))
            })
            .catch(error => {
                store.dispatch(createToast(toastFactory(UPDATE_SETTINGS.ERROR, error)))
            })
    }, 2200)

    handleHide = (showGroup: ShowGroupType) => {
        switch (showGroup) {
            case 'empty_images':
                this.setState({ projectSettings: { ...this.state.projectSettings, hide_by_no_image: !this.state.projectSettings.hide_by_no_image } })
                this.updateSettingsDebounced()
                break
            case 'unavailable':
                //status 403,404,500,0
                const isHidden = this.getIsHidden(showGroup)
                const newHideByStatusCode = isHidden ? null : '403|404|500|0'
                this.setState({ projectSettings: { ...this.state.projectSettings, hide_by_status_code: newHideByStatusCode } })
                this.updateSettingsDebounced()
                break
            case 'unsupported_mime_type':
                this.setState({ projectSettings: { ...this.state.projectSettings, hide_by_is_empty: !this.state.projectSettings.hide_by_is_empty } })
                this.updateSettingsDebounced()
                break
            case 'has_watermark':
                this.setState({ projectSettings: { ...this.state.projectSettings, hide_by_has_watermark: !this.state.projectSettings.hide_by_has_watermark } })
                this.updateSettingsDebounced()
                break
            case 'wrong_placement':
                this.setState({ projectSettings: { ...this.state.projectSettings, hide_by_wrong_placement: !this.state.projectSettings.hide_by_wrong_placement } })
                this.updateSettingsDebounced()
                break
            case 'low_quality':
                this.setState({ projectSettings: { ...this.state.projectSettings, hide_by_low_quality: !this.state.projectSettings.hide_by_low_quality } })
                this.updateSettingsDebounced()
                break
            case 'seo_large_file':
                this.setState({ projectSettings: { ...this.state.projectSettings, hide_by_seo_large_file: !this.state.projectSettings.hide_by_seo_large_file } })
                this.updateSettingsDebounced()
                break
        }
    }

    showExportModal = () => {
        //TODO!!!!! tady modal
    }

    handleExport = (showGroup: ShowGroupType) => {
        switch (showGroup) {
            case 'wrong_placement':
                this.props.createProductsExport({
                    ...emptyFiltersState,
                    image_validations: { ...emptyFiltersState.image_validations, status: ['NOT_OK'] },
                }, 'validations'
                )
                break;
            case 'has_watermark':
                this.props.createProductsExport({
                    ...emptyFiltersState,
                    watermark: { ...emptyFiltersState.watermark, status: ['NOT_OK'] },
                }, 'watermarks'
                )
                break;
            case 'transparent_background':
                this.props.createProductsExport({
                    ...emptyFiltersState,
                    image_validations: { ...emptyFiltersState.image_validations, has_alpha: 1 },
                }, 'validations'
                )
                break;
            case 'low_quality':
                this.props.createProductsExport({
                    ...emptyFiltersState,
                    quality: { ...emptyFiltersState.quality, status: ['NOT_OK'] },
                }, 'images_quality'
                )
                break;
            default:
                this.props.createProductsExport({
                    ...emptyFiltersState,
                    image: { ...emptyFiltersState.image, showGroup: [showGroup] },
                }, 'products'
                )
        }
    }

    transProductsCount = (count: number) => {
        if (count === 1) {
            return trans('one_product')
        } else if (count > 1 && count < 5) {
            return trans('less_than_five_products')
        } else {
            return trans('products_count')
        }
    }

    showProducts = (showGroup: ShowGroupType) => {
        switch (showGroup) {
            case 'wrong_placement':
                this.props.initForm('filtersForm', {
                    ...emptyFiltersState,
                    image: { ...emptyFiltersState.image },
                    product: { ...emptyFiltersState.product },
                    image_validations: { ...emptyFiltersState.image_validations, status:  ['NOT_OK'] }
                })
                this.props.router.navigate(`/eshop/${eshopId}/project/${projectId}/products/validations`)
                break;
            case 'has_watermark':
                this.props.initForm('filtersForm', {
                    ...emptyFiltersState,
                    image: { ...emptyFiltersState.image },
                    product: { ...emptyFiltersState.product },
                    watermark: { ...emptyFiltersState.watermark, status: ['NOT_OK'] }
                })
                this.props.router.navigate(`/eshop/${eshopId}/project/${projectId}/products/watermarks`)
                break;
            case 'has_person':
                this.props.initForm('filtersForm', {
                    ...emptyFiltersState,
                    person: { ...emptyFiltersState.person, max_confidence: {from: 25, to: 100} },
                })
                this.props.router.navigate(`/eshop/${eshopId}/project/${projectId}/products`)
                break;
            case 'transparent_background':
                this.props.initForm('filtersForm', {
                    ...emptyFiltersState,
                    image: { ...emptyFiltersState.image },
                    product: { ...emptyFiltersState.product },
                    image_validations: { ...emptyFiltersState.image_validations, has_alpha: 1 }
                })
                this.props.router.navigate(`/eshop/${eshopId}/project/${projectId}/products/validations`)
                break;
            case 'low_quality':
                this.props.initForm('filtersForm', {
                    ...emptyFiltersState,
                    image: { ...emptyFiltersState.image },
                    product: { ...emptyFiltersState.product },
                    quality: { ...emptyFiltersState.quality, confidence: {from: 25, to: 100} }
                })
                this.props.router.navigate(`/eshop/${eshopId}/project/${projectId}/products/images_quality`)
                break;
            default:
                this.props.initForm('filtersForm', {
                    ...emptyFiltersState,
                    image: { ...emptyFiltersState.image, showGroup: showGroup },
                    product: { ...emptyFiltersState.product },
                })
                this.props.router.navigate(`/eshop/${eshopId}/project/${projectId}/products`)
        }
    }

    getIsHidden = (showGroup: ShowGroupType) => {
        const { projectSettings } = this.state

        switch (showGroup) {
            case 'empty_images':
                return projectSettings.hide_by_no_image
            case 'unavailable':
                return projectSettings.hide_by_status_code && Array.isArray(projectSettings.hide_by_status_code) && (
                    projectSettings.hide_by_status_code.includes(403) || projectSettings.hide_by_status_code.includes(404) || projectSettings.hide_by_status_code.includes(500)
                )
            case 'unsupported_mime_type':
                return projectSettings.hide_by_is_empty
            case 'has_watermark':
                return projectSettings.hide_by_has_watermark
            case 'wrong_placement':
                return projectSettings.hide_by_wrong_placement
            case 'low_quality':
                return projectSettings.hide_by_low_quality
            case 'seo_large_file':
                return projectSettings.hide_by_seo_large_file
        }
    }

    renderRow = (type: RowType, count: number, text: string, hint: string, showGroup: ShowGroupType, hideable: boolean = true) => {
        const { projectSettings } = this.state
        let icon = 'exclamation-circle'
        let color = Theme.orange

        switch (type) {
            case 'problems':
                icon = 'times-circle'
                color = Theme.red
                break
            case 'warnings':
                icon = 'exclamation-circle'
                color = Theme.orange
                break
            case 'recommendations':
                icon = 'lightbulb-o'
                color = UglyYellow
                break
        }

        const isHidden = projectSettings ? this.getIsHidden(showGroup) : null

        return <DataRow dataId={type}>
            {count > 0 ?
                <DataCell style={{ cursor: 'pointer' }} onClick={() => this.showProducts(showGroup)}>
                    <span title={trans('show_products')}>
                        <Icon type={icon} size={iconSize} color={color} />
                        <span className={'text'}>{text}
                            &nbsp;
                            <PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{trans(hint)}</PopupHint>
                            {(isHidden && hideable) ?
                                <span style={{ color: iconDisabledColor }}>&nbsp;&nbsp;({trans('hidden_by_filter')})</span> :
                                <span />
                            }
                        </span>
                    </span>
                </DataCell> :
                <DataCell>
                    <Icon type={'check-circle'} size={iconSize} color={Theme.green} />
                    <span className={'text'} title={'ok'}>{text}&nbsp;
                        <PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{trans(hint)}</PopupHint>
                    </span>
                </DataCell>
            }

            {count > 0 ?
                <DataCell className={'right nowrap'}>
                    <span className={'text'}>{count}</span>
                    <span title={trans('export_to_csv')} onClick={() => {
                        this.handleExport(showGroup)
                        this.showExportModal()
                    }}>
                        <Icon type="arrow-circle-down" color={Theme.blue} size={iconSize} className={'link'} title={showGroup} />
                    </span>
                    <span title={trans('show_products')} onClick={() => this.showProducts(showGroup)}>
                        <Icon type="box-light" color={Theme.blue} size={iconSize} className={'link'} title={showGroup} />
                    </span>
                    {hideable && projectSettings &&
                        <span title={trans(isHidden ? 'unhide_products' : 'hide_products_by_filter')} onClick={() => this.handleHide(showGroup)}>
                            <Icon type={isHidden ? 'eye' : 'eye-slash'} color={Theme.blue} size={iconSize} className={'link'} title={showGroup} />
                        </span>
                    }
                </DataCell>
                :
                <DataCell className={'right'}>
                    <b>0</b>
                </DataCell>
            }
        </DataRow>
    }

    render() {
        const { otherDiagnostics, problems, warnings, recommendations, projectSettings } = this.state

        let chartData = []

        if (problems) {
            chartData.push({ name: trans('status_empty_images'), value: problems.missingImagesTotal, color: Theme.red })
            chartData.push({ name: trans('status_unsupported_mime_type'), value: problems.unsupportedFormatOrEmptyTotal, color: Theme.red + '60' })
            chartData.push({ name: trans('status_empty_images'), value: problems.missingImagesTotal, color: Theme.red })
        }

        if (warnings) {
            chartData.push({ name: trans('status_invalid_url'), value: warnings.invalidUrlTotal, color: Theme.orange })
            chartData.push({ name: trans('has_watermark'), value: warnings.withWatermarksTotal, color: Theme.orange + 'a0' })
            chartData.push({ name: trans('alternative_images'), value: warnings.tooManyAltImagesTotal, color: Theme.orange + '60' })
            chartData.push({ name: trans('unsupported_background'), value: warnings.unsupportedBackgroundTotal, color: UglyYellow + '60' })
        }

        if (recommendations) {
            chartData.push({ name: trans('status_few_alt_images'), value: recommendations.fewAltImagesTotal, color: Theme.steelblue + '60' })
            chartData.push({ name: trans('status_same_images'), value: recommendations.sameImagesTotal, color: UglyYellow })
            chartData.push({ name: trans('status_poor_quality_images'), value: recommendations.poorQualityTotal, color: UglyYellow + 'a0' })
            chartData.push({ name: trans('status_seo_large_file'), value: recommendations.seoLargeFilesTotal, color: UglyYellow + 'a0' })
            chartData.push({ name: trans('status_wrong_placement'), value: recommendations.badPlacementTotal, color: UglyYellow + '60' })
            chartData.push({ name: trans('not_recommended_colorspace'), value: recommendations.cmykColorSpaceTotal, color: UglyYellow + '60' })
            chartData.push({ name: trans('has_person'), value: recommendations.hasPersonTotal, color: UglyYellow + '60' })
        }

        chartData = chartData.filter(o => o.value > 0)

        return (
            <StyledGrid>
                <GridCell>
                    <StyledGroupSection header={trans('overview')} color={Theme.blue} suffix={<span>{warnings?.project_type ?? ''}</span>}>
                        <div style={{ textAlign: 'center', alignSelf: 'center', display: 'grid' }}>

                            {otherDiagnostics && otherDiagnostics.outputImageElement &&
                                <Grid cols={'max-content max-content'} gap={'20px'}>
                                    <GridCell>{trans('output_image_element')}:</GridCell>
                                    <GridCell style={{ textAlign: 'left', alignSelf: 'center' }}>
                                        <b>{otherDiagnostics.outputImageElement}</b>
                                        <br />
                                        {otherDiagnostics.isOutputElementHidden &&
                                            <Icon type={'exclamation'} text={trans('output_image_hidden_warning')} color={Theme.red} textFirst={true} />
                                        }
                                    </GridCell>
                                </Grid>
                            }

                            {(chartData.length > 0) ?
                                <Chart
                                    options={reversedBarOptions(chartData)}
                                    series={[{
                                        data: chartData.map(s => s.value),
                                    }]}
                                    type="bar"
                                    height={300}
                                /> : !!(problems && warnings && recommendations) ?
                                    <Icon type={'check-circle'} size={150} color={Theme.green} text={trans('status_200_0')} /> :
                                    <StyledLoader><FadeLoader color={Theme.blue} /></StyledLoader>
                            }
                        </div>
                    </StyledGroupSection>
                </GridCell>
                <GridCell>

                    <StyledGroupSection color={Theme.red} header={trans('problems')} id={'problems'}>
                        <p>{trans('problems_text')}</p>
                        {problems ?
                            <StyledProblemsTable>
                                <DataBody>
                                    {this.renderRow('problems', problems.missingImagesTotal, trans('status_empty_images'), 'status_empty_images_description', 'empty_images')}
                                    {this.renderRow('problems', problems.unavailableStatusTotal, trans('status_unavailable'), 'status_unavailable_description', 'unavailable')}
                                    {this.renderRow('problems', problems.unsupportedFormatOrEmptyTotal, trans('status_unsupported_mime_type'), 'status_unsupported_mime_type_description', 'unsupported_mime_type')}
                                </DataBody>
                            </StyledProblemsTable> : <StyledLoader><FadeLoader color={Theme.red} /></StyledLoader>
                        }
                    </StyledGroupSection>

                </GridCell>
                <GridCell>

                    <StyledGroupSection header={trans('warnings')} color={Theme.orange}>
                        <p>{trans('warnings_text')}</p>
                        {warnings ?
                            <StyledProblemsTable>
                                <DataBody>
                                    {this.renderRow('warnings', warnings.invalidUrlTotal, trans('status_invalid_url'), 'status_invalid_url_description', 'invalid_url', false)}
                                    {this.renderRow('warnings', warnings.withWatermarksTotal, trans('watermarks'), 'has_watermark_description', 'has_watermark')}

                                    {warnings.comparisonEngine && typeof warnings.comparisonEngine.max_alt_images !== "undefined" &&
                                        this.renderRow('warnings',
                                            warnings.tooManyAltImagesTotal,
                                            `${trans('alternative_images')} ` + (warnings.comparisonEngine.max_alt_images ? `(max ${warnings.comparisonEngine.max_alt_images})` : ''),
                                            'alternative_images_description',
                                            'too_many_alts')
                                    }

                                    {projectSettings && warnings.supportedFormats &&
                                        <SettingsRow
                                            isOk={!warnings.unsupportedProjectMimeTypeTotal}
                                            translatedText={`${trans('output_image_format')} (${warnings.projectOutputImageFormat})`}
                                            translatedHint={`${trans("unsupported_mime_by_engines")} ${warnings.project_type} ${trans('supports_formats')}: ${warnings.supportedFormats}`}
                                        />
                                    }
                                    {this.renderRow('warnings', warnings.unsupportedBackgroundTotal, trans('unsupported_background'), 'unsupported_background_description', 'unsupported_background', false)}
                                </DataBody>
                            </StyledProblemsTable> : <StyledLoader><FadeLoader color={Theme.orange} /></StyledLoader>
                        }
                    </StyledGroupSection>

                </GridCell>
                <GridCell>

                    <StyledGroupSection header={trans('recommendations')} color={UglyYellow}>
                        <p>{trans('recommendations_text')}</p>

                        {recommendations ?
                            <StyledProblemsTable>
                                <DataBody>
                                    {this.renderRow('recommendations', recommendations.badPlacementTotal, trans('status_wrong_placement'), 'status_wrong_placement_description', 'wrong_placement')}
                                    {this.renderRow('recommendations', recommendations.poorQualityTotal, trans('status_poor_quality_images'), 'status_poor_quality_images_description', 'low_quality')}
                                    {this.renderRow('recommendations', recommendations.seoLargeFilesTotal, trans('status_seo_large_file'), 'status_seo_large_file_description', 'seo_large_file')}
                                    {this.renderRow('recommendations', recommendations.cmykColorSpaceTotal, trans('not_recommended_colorspace'), 'not_recommended_colorspace_description', 'not_recommended_colorspace', false)}
                                    {this.renderRow('recommendations', recommendations.hasPersonTotal, trans('has_person'), 'has_person', 'has_person', false)}
                                    {this.renderRow('recommendations', recommendations.sameImagesTotal, trans('status_same_images'), 'status_same_images_description', 'same_images', false)}

                                    {warnings && warnings.comparisonEngine && typeof warnings.comparisonEngine.max_alt_images !== "undefined" &&
                                        this.renderRow('recommendations',
                                            recommendations.fewAltImagesTotal,
                                            `${trans('status_few_alt_images')} ` + (warnings.comparisonEngine.max_alt_images ? `(max ${warnings.comparisonEngine.max_alt_images})` : ''),
                                            'status_few_alt_images_description',
                                            'few_alt_images',
                                            false)
                                    }
                                </DataBody>
                            </StyledProblemsTable> : <StyledLoader><FadeLoader color={UglyYellow} /></StyledLoader>
                        }
                    </StyledGroupSection>

                </GridCell>
            </StyledGrid>
        )
    }
}

const StyledLoader = css('div')({
    textAlign: 'center',
    minHeight: '150px',
    padding: '50px calc(50% - 25px)',
})

const StyledGrid = css(Grid)({
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridGap: '20px',
    ' .centered': {
        alignSelf: 'center',
        textAlign: 'center',
    },
    ' .m-header': {
        fontWeight: 'medium',
        fontSize: '24px',
    },
    ' .m-suffix': {
        padding: '16px 10px',
        fontSize: '18px',
    },
    ' .m-content': {
        '@media screen and (max-width: 800px)': {
            padding: '5px !important',
        }
    },
    ' .right': {
        textAlign: 'right',
    },
    '@media screen and (max-width: 1024px)': {
        gridTemplateColumns: '1fr',
        paddingBottom: inIframe() ? '100px' : '10px',
        overflow: 'auto',
        ' td': {
            fontSize: '12px !important',
        }
    },
})

export const StyledGroupSection = css(Section)({
    '@media screen and (min-width: 1024px)': {
        height: '100%',
        overflow: 'hidden',
    },
    marginBottom: 0,
    background: 'transparent',
    ' .m-header-content': {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: '18px',
    },
    ' .muk-icon': {
        padding: '2px',
    },
    ' .link': {
        cursor: 'pointer',
    },
    ' .m-content': {
        background: '#ffffff7a',
        height: 'calc(100% - 50px)',
        overflow: 'hidden',
        display: 'grid',
    },
    ' .nowrap': {
        whiteSpace: 'nowrap',
    },
    ' .clickable': {
        cursor: 'pointer',
    },
    ' .selected': {
        color: Theme.blue,
        fontWeight: 'bold',
    }
})

const StyledProblemsTable = css(DataTable)({
    '@media screen and (min-width: 800px)': {
        lineHeight: '30px',
    },
    ' .text': {
        padding: '0 10px',
        fontWeight: 'bold',
    },
})


const SettingsRow = ({ translatedText, translatedHint, isOk }) => (
    <DataRow dataId={'settings'}>
        <DataCell style={{ cursor: 'pointer' }}>
            <span>
                {isOk ?
                    <Icon type={'check-circle'} size={iconSize} color={Theme.green} />
                    :
                    <Icon type={'exclamation-circle'} size={iconSize} color={Theme.orange} />
                }
                <span className={'text'}>{translatedText}&nbsp;<PopupHint icon={<Icon type={'info-circle'} color={iconDisabledColor} />}>{translatedHint}</PopupHint></span>
            </span>
        </DataCell>
        <DataCell className={'right nowrap'}>
            {!isOk ?
                <Link to={`/eshop/${eshopId}/project/${projectId}/settings/showProjectSettings`} title={trans('project_settings')}>
                    <Icon type="settings-light" color={Theme.blue} size={iconSize} className={'link'} />
                </Link>
                : <span style={{ fontWeight: 'bold' }}>0</span>}
        </DataCell>
    </DataRow>
)

const StyledDataTable = css(DataTable)({
    overflowX: 'auto',
    ' .m-header': {
        fontSize: '10px',
    },
    ' img.product-image': {
        maxWidth: '80px',
        maxHeight: '80px',
    },
    ' .elements-list': {
        maxHeight: '100px',
        overflowY: 'auto',
        fontSize: '10px',
        listStyle: 'none',
    },
    ' .blue': {
        color: Theme.blue,
    }
})

function mapStateToProps(state) {
    return {
    }
}

export default connect(mapStateToProps, {
    createProductsExport,
    initForm,
})(withRouter(Diagnostics))